import React from 'react';
import {
  Box,
  Skeleton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  alpha,
} from '@mui/material';

/* Skeleton stat tile matching StatTile in StocksList */
const StatTileSkeleton = ({ color }) => (
  <Paper elevation={0}
    sx={{
      position: 'relative', overflow: 'hidden',
      borderRadius: 2.5, border: `1px solid ${alpha(color, 0.15)}`,
      bgcolor: '#fff',
      p: '14px 16px', flex: 1, minWidth: 140,
    }}>
    <Box sx={{ position: 'absolute', top: 0, right: 0, width: 64, height: 64,
      background: `radial-gradient(circle at top right, ${alpha(color, 0.06)} 0%, transparent 70%)`,
      pointerEvents: 'none' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Skeleton variant="rounded" width={36} height={36}
        sx={{ borderRadius: 2, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Skeleton variant="text" width={48} height={28} />
          <Skeleton variant="text" width={36} height={16} />
        </Box>
        <Skeleton variant="text" width={64} height={14} sx={{ mt: 0.2 }} />
      </Box>
    </Box>
  </Paper>
);

function StocksListSkeleton() {
  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* ── HEADER ────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Skeleton variant="rounded" width={42} height={42}
            sx={{ borderRadius: 2.5, flexShrink: 0 }} />
          <Box>
            <Skeleton variant="text" width={200} height={28} />
            <Skeleton variant="text" width={280} height={18} sx={{ mt: 0.3 }} />
          </Box>
        </Box>

        {/* Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Skeleton variant="rounded" width={220} height={36} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: 2 }} />
          <Box sx={{ display: 'flex', gap: 0 }}>
            <Skeleton variant="rounded" width={100} height={34}
              sx={{ borderRadius: '4px 0 0 4px' }} />
            <Skeleton variant="rounded" width={100} height={34}
              sx={{ borderRadius: '0 4px 4px 0' }} />
          </Box>
        </Box>
      </Box>

      {/* ── STAT TILES ────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <StatTileSkeleton color="#3b82f6" />
        <StatTileSkeleton color="#22c55e" />
        <StatTileSkeleton color="#f59e0b" />
        <StatTileSkeleton color="#ef4444" />
      </Box>

      {/* ── DATA TABLE ────────────────────────────────── */}
      <Paper elevation={0} sx={{
        borderRadius: 2.5, border: '1px solid #e5e7eb', overflow: 'hidden',
      }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              {[50, 160, 70, 70, 70, 80, 90, 80].map((w, i) => (
                <TableCell key={i} sx={{ py: 1.5 }}>
                  <Skeleton variant="text" width={w} height={20} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: 12 }).map((_, row) => (
              <TableRow key={row} sx={{
                bgcolor: row % 2 === 0 ? '#fff' : '#fafbfc',
              }}>
                {/* Ticker */}
                <TableCell sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: 1.5 }} />
                    <Box>
                      <Skeleton variant="text" width={44} height={18} />
                      <Skeleton variant="text" width={32} height={12} />
                    </Box>
                  </Box>
                </TableCell>
                {/* Company */}
                <TableCell sx={{ py: 1.5 }}>
                  <Skeleton variant="text" width={120 + (row % 3) * 20} height={18} />
                  <Skeleton variant="text" width={60} height={14} />
                </TableCell>
                {/* Price */}
                <TableCell sx={{ py: 1.5 }}>
                  <Skeleton variant="text" width={55} height={18} />
                </TableCell>
                {/* Change */}
                <TableCell sx={{ py: 1.5 }}>
                  <Skeleton variant="rounded" width={52} height={22} sx={{ borderRadius: 1 }} />
                </TableCell>
                {/* Volume */}
                <TableCell sx={{ py: 1.5 }}>
                  <Skeleton variant="text" width={60} height={18} />
                </TableCell>
                {/* Market Cap */}
                <TableCell sx={{ py: 1.5 }}>
                  <Skeleton variant="text" width={65} height={18} />
                </TableCell>
                {/* Valuation */}
                <TableCell sx={{ py: 1.5 }}>
                  <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 3 }} />
                </TableCell>
                {/* Score / Signal */}
                <TableCell sx={{ py: 1.5 }}>
                  <Skeleton variant="rounded" width={60} height={24} sx={{ borderRadius: 3 }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1.5 }}>
          <Skeleton variant="rounded" width={340} height={36} sx={{ borderRadius: 1 }} />
        </Box>
      </Paper>

      {/* Animated shimmer effect */}
      <Box
        sx={{
          position: 'fixed',
          top: 0, left: 0, right: 0,
          height: 3,
          background: 'linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.5), transparent)',
          animation: 'shimmer 2s infinite',
          zIndex: 9999,
          '@keyframes shimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}
      />
    </Box>
  );
}

export default StocksListSkeleton;

